'use client';
import { useCallback, useEffect, useState } from 'react';
import { createContext } from 'react';
import Cookies from 'js-cookie';
import posthog from 'posthog-js';
import { usePathname } from 'next/navigation';
import { logout as logoutFromNewApi } from '@/services/userLoginApi';
import { getSelfOriginForRedirects } from '@/config/apiConfig';

export const getAuthData = () => {
  if (typeof window === 'undefined') {
    return {
      token: null,
      userId: null,
      isAuthenticated: false,
      verificationPending: false,
    };
  }
  
  let token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  // Cookie-based login API only returns userID; no token. Treat userId as authenticated.
  if (userId && !token) {
    token = userId; // backward compat for code that uses auth.token (e.g. old API calls)
  }
  let isAuthenticated = false,
    verificationPending = false;
  
  if (localStorage.getItem('verificationPending')) {
    isAuthenticated = false;
    verificationPending = true;
  } else {
    isAuthenticated = !!(token && userId);
    verificationPending = false;
  }
  
  return {
    token,
    userId,
    isAuthenticated,
    verificationPending,
  };
};

export function clearStorage() {
  if (typeof window === 'undefined') return;
  
  Object.keys(Cookies.get()).forEach(function (cookieName) {
    if (cookieName != 'acceptCookies') {
      Cookies.remove(cookieName);
    }
  });
  localStorage.clear();
  sessionStorage.clear();
  
  if (process.env.NEXT_PUBLIC_ENV_NAME != 'local') {
    location.href = `${getSelfOriginForRedirects()}/v2/login`;
  }
}

export const AuthContext = createContext();

const DEFAULT_AUTH_STATE = {
  isAuthenticated: false,
  verificationPending: false,
  userId: null,
  token: null,
  isLoading: true,
};

export default function Authentication({ children }) {
  const [authData, setAuthData] = useState(DEFAULT_AUTH_STATE);
  const pathname = usePathname();

  const logout = useCallback((force) => {
    logoutFromNewApi()
      .then(() => {
        clearStorage();
      })
      .catch((err) => {
        console.warn('Logout API failed:', err);
        if (force) clearStorage();
      });
  }, []);

  // Update auth data function
  const updateAuthData = useCallback((newAuthData) => {
    setAuthData(prev => ({
      ...prev,
      ...newAuthData,
      isLoading: false
    }));
  }, []);

  // Initialize auth data on mount
  useEffect(() => {
    const initAuth = () => {
      const authInfo = getAuthData();
      setAuthData({
        ...authInfo,
        isLoading: false
      });

      if (authInfo.userId) {
        posthog.identify(authInfo.userId);
      }
    };

    initAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ 
      ...authData, 
      setAuthData: updateAuthData, 
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
}